import React, { useEffect, useState, useRef, Component } from 'react';
import { motion, useInView } from 'framer-motion';
// Animated Counter Component
const AnimatedCounter: React.FC<{
  end: number;
  duration?: number;
  suffix?: string;
  prefix?: string;
  decimals?: number;
}> = ({
  end,
  duration = 2,
  suffix = '',
  prefix = '',
  decimals = 0
}) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true
  });
  useEffect(() => {
    if (!isInView) return;
    let startTime: number;
    let animationFrame: number;
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = (timestamp - startTime) / (duration * 1000);
      if (progress < 1) {
        const currentValue = end * progress;
        setCount(decimals > 0 ? parseFloat(currentValue.toFixed(decimals)) : Math.floor(currentValue));
        animationFrame = requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };
    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [isInView, end, duration, decimals]);
  return <span ref={ref} data-id="element-2155">
      {prefix}
      {count}
      {suffix}
    </span>;
};
// Logo Component
const Logo: React.FC = () => <motion.a href="/" initial={{
  opacity: 0,
  y: -20
}} animate={{
  opacity: 1,
  y: 0
}} transition={{
  duration: 0.6
}} className="absolute top-4 right-4 md:top-8 md:right-8 z-50 group" data-id="element-2156">
    <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/nrvkiu6sNi7Rupzm8hWkWo/yellow_full_logo.png" alt="HIRE" className="h-6 md:h-8 w-auto transition-transform group-hover:scale-105" data-id="element-2157" />
  </motion.a>;
// Page Container Component
const Page: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({
  children,
  className = ''
}) => <motion.div initial={{
  opacity: 0
}} whileInView={{
  opacity: 1
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.6
}} className={`min-h-screen w-full flex flex-col relative ${className}`} data-id="element-2158">
    <Logo data-id="element-2159" />
    {children}
  </motion.div>;
// Data Box Component
const DataBox: React.FC<{
  label: string;
  value: string;
}> = ({
  label,
  value
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true
}} transition={{
  duration: 0.5
}} className="border-l-4 border-amber pl-4 py-2" data-id="element-2160">
    <div className="text-slate-400 text-sm font-medium uppercase tracking-wider" data-id="element-2161">
      {label}
    </div>
    <div className="text-white text-lg font-semibold mt-1" data-id="element-2162">{value}</div>
  </motion.div>;
// Problem Item Component
const ProblemItem: React.FC<{
  text: string;
  index: number;
}> = ({
  text,
  index
}) => <motion.div initial={{
  opacity: 0,
  x: -20
}} whileInView={{
  opacity: 1,
  x: 0
}} viewport={{
  once: true
}} transition={{
  duration: 0.5,
  delay: index * 0.1
}} className="flex items-start gap-3 mb-4" data-id="element-2163">
    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center mt-1" data-id="element-2164">
      <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-2165">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" data-id="element-2166" />
      </svg>
    </div>
    <div className="text-slate-300 leading-relaxed" data-id="element-2167">{text}</div>
  </motion.div>;
// Process Step Component
const ProcessStep: React.FC<{
  number: string;
  title: string;
  description: string;
  index: number;
}> = ({
  number,
  title,
  description,
  index
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true
}} transition={{
  duration: 0.6,
  delay: index * 0.15
}} className="relative" data-id="element-2168">
    <div className="flex items-start gap-4" data-id="element-2169">
      <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-amber flex items-center justify-center" data-id="element-2170">
        <span className="text-navy text-xl font-bold" data-id="element-2171">{number}</span>
      </div>
      <div className="flex-1" data-id="element-2172">
        <h3 className="text-white text-xl font-bold mb-2" data-id="element-2173">{title}</h3>
        <p className="text-slate-300 leading-relaxed" data-id="element-2174">{description}</p>
      </div>
    </div>
  </motion.div>;
// Stat Card Component
const StatCard: React.FC<{
  value: string;
  label: string;
  index: number;
  highlight?: boolean;
}> = ({
  value,
  label,
  index,
  highlight = false
}) => <motion.div initial={{
  opacity: 0,
  scale: 0.9
}} whileInView={{
  opacity: 1,
  scale: 1
}} viewport={{
  once: true
}} transition={{
  duration: 0.5,
  delay: index * 0.1
}} className={`p-6 rounded-lg ${highlight ? 'bg-amber' : 'bg-white'} border-2 ${highlight ? 'border-amber-dark' : 'border-slate-200'}`} data-id="element-2175">
    <div className={`text-4xl font-bold mb-2 ${highlight ? 'text-navy' : 'text-amber'}`} data-id="element-2176">
      {value}
    </div>
    <div className={`text-sm font-medium uppercase tracking-wider ${highlight ? 'text-navy-light' : 'text-slate-600'}`} data-id="element-2177">
      {label}
    </div>
  </motion.div>;
export function WindowsCaseStudy() {
  return <div className="w-full bg-slate-50 font-sans" data-id="element-2178">
      {/* PAGE 1: COVER */}
      <Page className="bg-navy items-center justify-center text-center px-8" data-id="element-2179">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="max-w-4xl mx-auto" data-id="element-2180">
          <div className="mb-12" data-id="element-2181">
            <div className="text-amber text-sm font-bold uppercase tracking-widest mb-4" data-id="element-2182">
              The Operator's Playbook
            </div>
            <h1 className="text-6xl md:text-7xl font-bold text-white mb-4 leading-tight" data-id="element-2183">
              The HIRE Advantage
            </h1>
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.6
        }} className="mb-8" data-id="element-2184">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 leading-tight" data-id="element-2185">
              CASE STUDY:
              <br data-id="element-2186" />
              The 5-Market Expansion Playbook
            </h2>
            <p className="text-xl md:text-2xl text-amber font-semibold leading-relaxed" data-id="element-2187">
              How We Recruited 39 Reps in 30 Days, Launched 3 New Markets, and
              Supported Over 250 Full-Commission Offers
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 1
        }} className="text-slate-400 text-lg" data-id="element-2188">
            A Field Report for D2D Operators
          </motion.div>
        </motion.div>
      </Page>

      {/* PAGE 2: COMPANY SNAPSHOT */}
      <Page className="bg-slate-50 px-8 py-16" data-id="element-2189">
        <div className="max-w-6xl mx-auto" data-id="element-2190">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="mb-12" data-id="element-2191">
            <h2 className="text-4xl font-bold text-navy mb-4" data-id="element-2192">
              Company Snapshot
            </h2>
            <div className="w-20 h-1 bg-amber mb-6" data-id="element-2193"></div>
            <p className="text-slate-700 text-xl leading-relaxed max-w-4xl" data-id="element-2194">
              This Colorado-based residential home-services company had a strong
              leadership foundation, a proven product, and a vision for
              multi-market expansion. But like many fast-growing organizations,
              they lacked a fully developed recruiting engine to support
              sustained scale.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 mb-12" data-id="element-2195">
            {/* Left Column: Snapshot Table */}
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="bg-navy p-8 rounded-lg" data-id="element-2196">
              <h3 className="text-amber text-xl font-bold mb-6 uppercase tracking-wider" data-id="element-2197">
                Snapshot Overview
              </h3>
              <div className="space-y-6" data-id="element-2198">
                <DataBox label="Industry" value="Residential Window Replacement" data-id="element-2199" />
                <DataBox label="Location" value="Colorado → Mountain West" data-id="element-2200" />
                <DataBox label="Team Size (Before)" value="Strong veteran core + inconsistent rookie flow" data-id="element-2201" />
                <DataBox label="Team Size (After)" value="5 markets with consistent weekly hires" data-id="element-2202" />
                <DataBox label="Primary Service" value="In-home window replacement" data-id="element-2203" />
                <DataBox label="Markets" value="Colorado Springs → Denver → Spokane → Tucson → Phoenix" data-id="element-2204" />
              </div>
            </motion.div>

            {/* Right Column: Context */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-2205">
              <h3 className="text-navy text-2xl font-bold mb-6" data-id="element-2206">
                The Starting Point
              </h3>
              <div className="space-y-4 text-slate-700 leading-relaxed" data-id="element-2207">
                <p data-id="element-2208">
                  They entered the partnership already operating two markets —
                  Colorado Springs and Denver — and wanted to expand even
                  faster.
                </p>
                <p data-id="element-2209">To do that, they needed:</p>
                <ul className="space-y-2 ml-6" data-id="element-2210">
                  <li className="flex items-start gap-2" data-id="element-2211">
                    <span className="text-amber mt-1" data-id="element-2212">•</span>
                    <span data-id="element-2213">Consistent rookie flow</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-2214">
                    <span className="text-amber mt-1" data-id="element-2215">•</span>
                    <span data-id="element-2216">A stronger pipeline process</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-2217">
                    <span className="text-amber mt-1" data-id="element-2218">•</span>
                    <span data-id="element-2219">
                      A system that could scale into additional territories
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-2220">
                    <span className="text-amber mt-1" data-id="element-2221">•</span>
                    <span data-id="element-2222">
                      Without overwhelming leadership or distracting from
                      production and culture
                    </span>
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="bg-amber/10 border-l-4 border-amber p-6 rounded-r-lg" data-id="element-2223">
            <p className="text-navy text-lg font-semibold" data-id="element-2224">
              The opportunity was enormous. They simply needed a scalable system
              to match it.
            </p>
          </motion.div>
        </div>
      </Page>

      {/* PAGE 3: THE "BEFORE" STATE */}
      <Page className="bg-navy px-8 py-16" data-id="element-2225">
        <div className="max-w-6xl mx-auto" data-id="element-2226">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="mb-12" data-id="element-2227">
            <h2 className="text-4xl font-bold text-white mb-4" data-id="element-2228">
              Starting Point: Their "Before" State
            </h2>
            <div className="w-20 h-1 bg-amber mb-6" data-id="element-2229"></div>
            <p className="text-slate-300 text-xl leading-relaxed max-w-4xl" data-id="element-2230">
              When we first met, the company wasn't struggling from lack of
              opportunity — they were struggling from underdeveloped recruiting
              infrastructure that capped their growth potential.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 mb-12" data-id="element-2231">
            <div data-id="element-2232">
              <h3 className="text-amber text-2xl font-bold mb-6" data-id="element-2233">
                Core Hiring Problems
              </h3>
              <div className="space-y-1" data-id="element-2234">
                <ProblemItem index={0} text="Underdeveloped Infrastructure: Lightly structured, largely manual, dependent on leadership time, vulnerable to inconsistency. They were generating applicants but lacked reliable filtering, qualifying, scheduling, warming, and integration." data-id="element-2235" />
                <ProblemItem index={1} text="Leadership Bottleneck: Managers stretched thin across recruiting, training, onboarding, daily support, personal production, and market expansion. Doing everything right — just too much manually." data-id="element-2236" />
                <ProblemItem index={2} text="Early-Stage Drop-Off: Without structured pre-launch warmup, scripted integration, predictable accountability, and consistent touchpoints, many rookies who should have succeeded didn't make it to Day 1." data-id="element-2237" />
                <ProblemItem index={3} text="Inconsistent Rookie Flow: The struggle wasn't hiring forever — it was consistency. Without weekly momentum, veterans plateau, leadership loses bandwidth, new markets get delayed, and revenue becomes inconsistent." data-id="element-2238" />
              </div>
            </div>

            <div data-id="element-2239">
              <h3 className="text-amber text-2xl font-bold mb-6" data-id="element-2240">
                Previous Strategy
              </h3>
              <div className="bg-slate-800 p-6 rounded-lg mb-6" data-id="element-2241">
                <p className="text-slate-300 leading-relaxed mb-4" data-id="element-2242">
                  Before partnering, their process included:
                </p>
                <ul className="space-y-3 text-slate-300" data-id="element-2243">
                  <li className="flex items-start gap-2" data-id="element-2244">
                    <span className="text-red-400 mt-1" data-id="element-2245">×</span>
                    <span data-id="element-2246">Basic job postings</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-2247">
                    <span className="text-red-400 mt-1" data-id="element-2248">×</span>
                    <span data-id="element-2249">Manual interview scheduling</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-2250">
                    <span className="text-red-400 mt-1" data-id="element-2251">×</span>
                    <span data-id="element-2252">Gut-feeling qualification</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-2253">
                    <span className="text-red-400 mt-1" data-id="element-2254">×</span>
                    <span data-id="element-2255">Limited pre-training</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-2256">
                    <span className="text-red-400 mt-1" data-id="element-2257">×</span>
                    <span data-id="element-2258">Inconsistent follow-up</span>
                  </li>
                </ul>
              </div>

              <div className="bg-amber/10 border-l-4 border-amber p-6 rounded-r-lg" data-id="element-2259">
                <p className="text-white font-semibold mb-2" data-id="element-2260">
                  The Operational Cost
                </p>
                <p className="text-slate-300 leading-relaxed" data-id="element-2261">
                  Leaders were spread too thin. Recruiting felt reactive. Growth
                  opportunities were slipping. Markets were stalling due to lack
                  of rookies. The pain wasn't hiring — the pain was not having a
                  system.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Page>

      {/* PAGE 4: GOALS */}
      <Page className="bg-slate-50 px-8 py-16" data-id="element-2262">
        <div className="max-w-6xl mx-auto" data-id="element-2263">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="mb-12 text-center" data-id="element-2264">
            <h2 className="text-4xl font-bold text-navy mb-4" data-id="element-2265">
              Goals: What We Set Out to Solve
            </h2>
            <div className="w-20 h-1 bg-amber mx-auto mb-6" data-id="element-2266"></div>
            <p className="text-slate-600 text-xl max-w-3xl mx-auto" data-id="element-2267">
              The goals were measurable, strategic, and aligned with their
              long-term expansion plan.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-2268">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="bg-white p-8 rounded-lg border-2 border-slate-200" data-id="element-2269">
              <div className="w-12 h-12 bg-amber rounded-lg flex items-center justify-center mb-4" data-id="element-2270">
                <span className="text-navy text-2xl font-bold" data-id="element-2271">1</span>
              </div>
              <h3 className="text-navy text-xl font-bold mb-3" data-id="element-2272">
                Build a Predictable Weekly Recruiting Engine
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-2273">
                A system that generates quality applicants, screens them
                quickly, moves them through the pipeline, and produces
                consistent weekly hires. Week in, week out.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="bg-white p-8 rounded-lg border-2 border-slate-200" data-id="element-2274">
              <div className="w-12 h-12 bg-amber rounded-lg flex items-center justify-center mb-4" data-id="element-2275">
                <span className="text-navy text-2xl font-bold" data-id="element-2276">2</span>
              </div>
              <h3 className="text-navy text-xl font-bold mb-3" data-id="element-2277">
                Strengthen Qualification & Screening
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-2278">
                Using targeted ad buying, sales-driven copy, optimized job
                placement, application filtering, multi-layer screening, fast
                scheduling, aggressive follow-up, and interview show-rate
                optimization.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="bg-white p-8 rounded-lg border-2 border-slate-200" data-id="element-2279">
              <div className="w-12 h-12 bg-amber rounded-lg flex items-center justify-center mb-4" data-id="element-2280">
                <span className="text-navy text-2xl font-bold" data-id="element-2281">3</span>
              </div>
              <h3 className="text-navy text-xl font-bold mb-3" data-id="element-2282">
                Improve Early-Stage Commitment & Integration
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-2283">
                With a pre-launch warmup framework, script test, accountability
                structure, clear expectations, and multi-day touchpoint rhythm.
                This dramatically increases both show-up and early retention.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.3
          }} className="bg-white p-8 rounded-lg border-2 border-slate-200" data-id="element-2284">
              <div className="w-12 h-12 bg-amber rounded-lg flex items-center justify-center mb-4" data-id="element-2285">
                <span className="text-navy text-2xl font-bold" data-id="element-2286">4</span>
              </div>
              <h3 className="text-navy text-xl font-bold mb-3" data-id="element-2287">
                Enable Multi-Market Expansion
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-2288">
                Their leadership wanted to scale quickly and intelligently. The
                recruiting engine needed to support opening Spokane, Tucson, and
                Phoenix without sacrificing quality or burning out their team.
              </p>
            </motion.div>
          </div>
        </div>
      </Page>

      {/* PAGE 5: THE SOLUTION */}
      <Page className="bg-navy px-8 py-16" data-id="element-2289">
        <div className="max-w-6xl mx-auto" data-id="element-2290">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="mb-12 text-center" data-id="element-2291">
            <h2 className="text-4xl font-bold text-white mb-4" data-id="element-2292">
              What We Built: The HIRE Advantage
            </h2>
            <div className="w-20 h-1 bg-amber mx-auto mb-6" data-id="element-2293"></div>
            <p className="text-slate-300 text-xl max-w-3xl mx-auto" data-id="element-2294">
              We implemented the proven HIRE recruiting engine — the same system
              we use to help companies scale consistently and sustainably.
            </p>
          </motion.div>

          <div className="grid gap-8 mb-12" data-id="element-2295">
            <ProcessStep index={0} number="01" title="Conversion-Optimized Strategy" description="High-performance ad copy and strategic job placement designed to attract quality candidates at scale." data-id="element-2296" />
            <ProcessStep index={1} number="02" title="The Filter" description="Multi-layer screening and application filters to remove low-intent candidates before they consume leadership time." data-id="element-2297" />
            <ProcessStep index={2} number="03" title="The Automation" description="Automated scheduling and aggressive show-rate support to eliminate the ghosting issue and maximize interview conversion." data-id="element-2298" />
            <ProcessStep index={3} number="04" title="The Launch" description="Integrated onboarding and sales-school readiness processes to ensure rookies show up prepared and committed." data-id="element-2299" />
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="bg-slate-800 p-8 rounded-lg border-2 border-amber" data-id="element-2300">
            <p className="text-white text-lg text-center font-medium" data-id="element-2301">
              No custom software. No special exceptions.
              <br data-id="element-2302" />
              <span className="text-amber font-bold" data-id="element-2303">
                Just a world-class system executed well.
              </span>
            </p>
          </motion.div>
        </div>
      </Page>

      {/* PAGE 6: THE RESULTS */}
      <Page className="bg-slate-50 px-8 py-16" data-id="element-2304">
        <div className="max-w-6xl mx-auto" data-id="element-2305">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="mb-12 text-center" data-id="element-2306">
            <h2 className="text-5xl font-bold text-navy mb-4" data-id="element-2307">The Results</h2>
            <div className="w-20 h-1 bg-amber mx-auto mb-6" data-id="element-2308"></div>
            <p className="text-slate-600 text-xl" data-id="element-2309">
              The performance gains were immediate and sustained
            </p>
          </motion.div>

          {/* Hero Stats */}
          <div className="grid md:grid-cols-2 gap-8 mb-16" data-id="element-2310">
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="bg-amber p-12 rounded-lg text-center" data-id="element-2311">
              <div className="text-7xl font-bold text-navy mb-4" data-id="element-2312">
                <AnimatedCounter end={39} data-id="element-2313" />
              </div>
              <div className="text-navy text-2xl font-bold uppercase tracking-wider" data-id="element-2314">
                New Hires
              </div>
              <div className="text-navy-light text-lg mt-2" data-id="element-2315">in 30 Days</div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="bg-navy p-12 rounded-lg text-center" data-id="element-2316">
              <div className="text-7xl font-bold text-amber mb-4" data-id="element-2317">
                <AnimatedCounter end={250} suffix="+" data-id="element-2318" />
              </div>
              <div className="text-white text-2xl font-bold uppercase tracking-wider" data-id="element-2319">
                Total Offers
              </div>
              <div className="text-slate-400 text-lg mt-2" data-id="element-2320">
                Across 5 Markets
              </div>
            </motion.div>
          </div>

          {/* Detailed Stats Grid */}
          <div className="grid md:grid-cols-4 gap-6 mb-12" data-id="element-2321">
            <StatCard index={0} value="13" label="Hires in First 10 Days" data-id="element-2322" />
            <StatCard index={1} value="74%" label="Conversion Rate" highlight data-id="element-2323" />
            <StatCard index={2} value="50%+" label="Show Rate" data-id="element-2324" />
            <StatCard index={3} value="$1.2M+" label="Spokane Revenue" highlight data-id="element-2325" />
          </div>

          {/* Timeline */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="bg-white p-8 rounded-lg border-2 border-slate-200 mb-12" data-id="element-2326">
            <h3 className="text-2xl font-bold text-navy mb-6" data-id="element-2327">
              Expansion Timeline
            </h3>
            <div className="space-y-6" data-id="element-2328">
              <div className="flex items-start gap-4" data-id="element-2329">
                <div className="flex-shrink-0 w-32 text-amber font-bold" data-id="element-2330">
                  First 10 Days
                </div>
                <div className="flex-1 text-slate-700" data-id="element-2331">
                  <span className="font-semibold text-navy" data-id="element-2332">13 Hires</span> made
                  immediately with 50%+ show rates. Managers freed to focus on
                  leadership, not logistics.
                </div>
              </div>
              <div className="flex items-start gap-4" data-id="element-2333">
                <div className="flex-shrink-0 w-32 text-amber font-bold" data-id="element-2334">
                  First 30 Days
                </div>
                <div className="flex-1 text-slate-700" data-id="element-2335">
                  <span className="font-semibold text-navy" data-id="element-2336">
                    53 interviews → 39 hires
                  </span>
                  . System efficiency dramatically increased. Rookie flow
                  stabilized. Leaders gained bandwidth. Expansion planning
                  accelerated.
                </div>
              </div>
              <div className="flex items-start gap-4" data-id="element-2337">
                <div className="flex-shrink-0 w-32 text-amber font-bold" data-id="element-2338">
                  Market Expansion
                </div>
                <div className="flex-1 text-slate-700" data-id="element-2339">
                  Launched{' '}
                  <span className="font-semibold text-navy" data-id="element-2340">
                    Spokane, Tucson, and Phoenix
                  </span>
                  . Spokane quickly became their top-producing branch,
                  generating over $1.2M in revenue within the first few months.
                </div>
              </div>
              <div className="flex items-start gap-4" data-id="element-2341">
                <div className="flex-shrink-0 w-32 text-amber font-bold" data-id="element-2342">
                  First 60-75 Days
                </div>
                <div className="flex-1 text-slate-700" data-id="element-2343">
                  Revenue generated by rookies created{' '}
                  <span className="font-semibold text-navy" data-id="element-2344">
                    break-even ROI in just over 2 months
                  </span>
                  . From that point onward, the recruiting engine functionally
                  built and scaled the team at net zero cost.
                </div>
              </div>
              <div className="flex items-start gap-4" data-id="element-2345">
                <div className="flex-shrink-0 w-32 text-amber font-bold" data-id="element-2346">
                  To Date
                </div>
                <div className="flex-1 text-slate-700" data-id="element-2347">
                  <span className="font-semibold text-navy" data-id="element-2348">
                    250+ Full-Commission Offers
                  </span>{' '}
                  across five markets. In full-commission D2D, you scale by
                  keeping top performers and consistently generating strong
                  rookie flow.
                </div>
              </div>
            </div>
          </motion.div>

          {/* Key Insight */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="bg-navy p-8 rounded-lg text-center" data-id="element-2349">
            <p className="text-white text-xl leading-relaxed" data-id="element-2350">
              <span className="text-amber font-bold" data-id="element-2351">This pace is rare</span> in
              full-commission D2D environments — but absolutely achievable with
              the right system.
            </p>
          </motion.div>
        </div>
      </Page>

      {/* PAGE 7: THE PARTNERSHIP */}
      <Page className="bg-navy px-8 py-16" data-id="element-2352">
        <div className="max-w-6xl mx-auto" data-id="element-2353">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="mb-12" data-id="element-2354">
            <h2 className="text-4xl font-bold text-white mb-4" data-id="element-2355">
              The True Growth Partnership
            </h2>
            <div className="w-20 h-1 bg-amber mb-6" data-id="element-2356"></div>
            <p className="text-slate-300 text-xl" data-id="element-2357">
              We didn't just build the engine and leave. We partnered deeply
              with leadership to ensure the system stayed powerful.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 mb-12" data-id="element-2358">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="bg-slate-800 p-8 rounded-lg border-l-4 border-amber" data-id="element-2359">
              <h3 className="text-amber text-xl font-bold mb-4 uppercase tracking-wider" data-id="element-2360">
                Our Role
              </h3>
              <ul className="space-y-3 text-slate-300" data-id="element-2361">
                <li className="flex items-start gap-2" data-id="element-2362">
                  <span className="text-amber mt-1" data-id="element-2363">✓</span>
                  <span data-id="element-2364">Upskilled managers</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-2365">
                  <span className="text-amber mt-1" data-id="element-2366">✓</span>
                  <span data-id="element-2367">Supported their full-time recruiter</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-2368">
                  <span className="text-amber mt-1" data-id="element-2369">✓</span>
                  <span data-id="element-2370">Trained leaders on rookie integration</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-2371">
                  <span className="text-amber mt-1" data-id="element-2372">✓</span>
                  <span data-id="element-2373">Reinforced a culture of recruiting</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-2374">
                  <span className="text-amber mt-1" data-id="element-2375">✓</span>
                  <span data-id="element-2376">Strengthened accountability rhythms</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-2377">
                  <span className="text-amber mt-1" data-id="element-2378">✓</span>
                  <span data-id="element-2379">
                    Built long-term recruiting behavior into their DNA
                  </span>
                </li>
              </ul>
              <p className="text-slate-300 leading-relaxed mt-6 pt-6 border-t border-slate-700" data-id="element-2380">
                Our job is to build and maintain the machine.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="bg-slate-800 p-8 rounded-lg border-l-4 border-amber" data-id="element-2381">
              <h3 className="text-amber text-xl font-bold mb-4 uppercase tracking-wider" data-id="element-2382">
                Their Role
              </h3>
              <ul className="space-y-3 text-slate-300" data-id="element-2383">
                <li className="flex items-start gap-2" data-id="element-2384">
                  <span className="text-amber mt-1" data-id="element-2385">✓</span>
                  <span data-id="element-2386">Lead</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-2387">
                  <span className="text-amber mt-1" data-id="element-2388">✓</span>
                  <span data-id="element-2389">Train</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-2390">
                  <span className="text-amber mt-1" data-id="element-2391">✓</span>
                  <span data-id="element-2392">Develop</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-2393">
                  <span className="text-amber mt-1" data-id="element-2394">✓</span>
                  <span data-id="element-2395">Support</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-2396">
                  <span className="text-amber mt-1" data-id="element-2397">✓</span>
                  <span data-id="element-2398">Execute at a high level</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-2399">
                  <span className="text-amber mt-1" data-id="element-2400">✓</span>
                  <span data-id="element-2401">Build culture</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-2402">
                  <span className="text-amber mt-1" data-id="element-2403">✓</span>
                  <span data-id="element-2404">Drive performance</span>
                </li>
                <li className="flex items-start gap-2" data-id="element-2405">
                  <span className="text-amber mt-1" data-id="element-2406">✓</span>
                  <span data-id="element-2407">Grow markets</span>
                </li>
              </ul>
              <p className="text-slate-300 leading-relaxed mt-6 pt-6 border-t border-slate-700" data-id="element-2408">
                Their leadership team did exactly what they do best.
              </p>
            </motion.div>
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="bg-amber p-10 rounded-lg text-center" data-id="element-2409">
            <h3 className="text-navy text-3xl font-bold mb-6" data-id="element-2410">The ROI</h3>
            <div className="grid md:grid-cols-2 gap-8 text-left" data-id="element-2411">
              <div data-id="element-2412">
                <div className="text-navy font-bold text-lg mb-2" data-id="element-2413">
                  Self-Funding in 60-75 Days
                </div>
                <div className="text-navy-light" data-id="element-2414">
                  Revenue generated by rookies created break-even ROI in just
                  over 2 months. Although install timelines extended the cash
                  cycle, the system itself became self-funding faster than
                  projected.
                </div>
              </div>
              <div data-id="element-2415">
                <div className="text-navy font-bold text-lg mb-2" data-id="element-2416">
                  Net Zero Cost at Scale
                </div>
                <div className="text-navy-light" data-id="element-2417">
                  From that point forward, the recruiting engine functionally
                  built and scaled the team at net zero cost. Together, the
                  partnership created multiple seven figures in revenue and a
                  blueprint for continued expansion.
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </Page>

      {/* PAGE 8: SUMMARY & CTA */}
      <Page className="bg-slate-50 px-8 py-16" data-id="element-2418">
        <div className="max-w-4xl mx-auto text-center" data-id="element-2419">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="mb-12" data-id="element-2420">
            <h2 className="text-5xl font-bold text-navy mb-4" data-id="element-2421">Final Summary</h2>
            <div className="w-20 h-1 bg-amber mx-auto mb-6" data-id="element-2422"></div>
            <p className="text-slate-700 text-xl leading-relaxed" data-id="element-2423">
              This case study is a clear example of what happens when a strong
              company, a committed leadership team, and the right recruiting
              engine come together with alignment and clarity.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="mb-12" data-id="element-2424">
            <div className="grid md:grid-cols-2 gap-6 mb-8" data-id="element-2425">
              <div className="bg-navy p-6 rounded-lg" data-id="element-2426">
                <div className="text-4xl font-bold text-amber mb-2" data-id="element-2427">39</div>
                <div className="text-white text-sm" data-id="element-2428">Hires in 30 Days</div>
              </div>
              <div className="bg-navy p-6 rounded-lg" data-id="element-2429">
                <div className="text-4xl font-bold text-amber mb-2" data-id="element-2430">13</div>
                <div className="text-white text-sm" data-id="element-2431">Hires in First 10 Days</div>
              </div>
              <div className="bg-navy p-6 rounded-lg" data-id="element-2432">
                <div className="text-4xl font-bold text-amber mb-2" data-id="element-2433">74%</div>
                <div className="text-white text-sm" data-id="element-2434">
                  Interview Conversion Rate
                </div>
              </div>
              <div className="bg-navy p-6 rounded-lg" data-id="element-2435">
                <div className="text-4xl font-bold text-amber mb-2" data-id="element-2436">3</div>
                <div className="text-white text-sm" data-id="element-2437">New Markets Launched</div>
              </div>
              <div className="bg-navy p-6 rounded-lg" data-id="element-2438">
                <div className="text-4xl font-bold text-amber mb-2" data-id="element-2439">$1.2M+</div>
                <div className="text-white text-sm" data-id="element-2440">Spokane Market Revenue</div>
              </div>
              <div className="bg-navy p-6 rounded-lg" data-id="element-2441">
                <div className="text-4xl font-bold text-amber mb-2" data-id="element-2442">250+</div>
                <div className="text-white text-sm" data-id="element-2443">
                  Total Full-Commission Offers
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="bg-navy p-12 rounded-lg mb-8" data-id="element-2444">
            <p className="text-white text-2xl leading-relaxed mb-6" data-id="element-2445">
              This case study proves what happens when a strong company meets
              the right recruiting engine.
            </p>
            <p className="text-amber text-xl font-bold" data-id="element-2446">
              Give leaders a predictable recruiting engine, and they can scale
              into any market they choose.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.6
        }} className="bg-amber p-10 rounded-lg" data-id="element-2447">
            <h3 className="text-navy text-2xl font-bold mb-4" data-id="element-2448">
              Ready to Install Your System?
            </h3>
            <p className="text-navy-light text-lg mb-6" data-id="element-2449">
              Schedule a Strategy Call and discover how The HIRE Advantage can
              transform your recruiting infrastructure.
            </p>
            <a href="/book-call" className="inline-block bg-navy hover:bg-navy-light text-white font-bold px-8 py-4 rounded-lg transition-colors text-lg" data-id="element-2450">
              Schedule Your Strategy Call →
            </a>
          </motion.div>
        </div>
      </Page>
    </div>;
}